/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 * @author Nikolay Sviridov
 * @version 1.35.0
 * @since 1.35.0
 */

import React, { useState, type FC, useMemo, useEffect, useCallback, useLayoutEffect } from 'react';

import callsIcon from '@megafon/ui-icons/basic-16-call_16.svg';
import peopleIcon from '@megafon/ui-icons/basic-16-many-people_16.svg';
import smsIcon from '@megafon/ui-icons/basic-16-message_16.svg';
import internetIcon from '@megafon/ui-icons/basic-16-mobile-internet_16.svg';
import servicesIcon from '@megafon/ui-icons/basic-16-services_16.svg';
import { Typography } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import ExternalLink, { ELinkTarget } from '@/main/components/common/ExternalLink/ExternalLink';
import { makeSelectTranslationCommon, makeSelectTranslationPageProducts } from '@/main/modules/internationalization/intSelectors';
import declinationWholeWord from '@/main/utils/declinationWholeWord';
import { colors } from '@theme/index';

import { setSelectedRoamingOption, setTariffsAndServicesItemSelected } from '../../tariffsAndServicesActions';
import { EProductsCart } from '../../tariffsAndServicesConstants';
import { makeSelectProductsCategorySelected, makeSelectProductsMeta, makeSelectRoamingOptionsSelected } from '../../tariffsAndServicesSelectors';
import { IRoamingOption } from '../../tariffsAndServicesTypes';
import {
	DividerHrStyled,
	LinkContainer,
	RoamingOptionCallsButtonStyled,
	RoamingOptionCardBottom,
	RoamingOptionCardInfoStyled,
	RoamingOptionCardServiceDescriptionStyled,
	RoamingOptionCardServiceInfoStyled,
	RoamingOptionCardStyled,
	RoamingOptionIntenetButtonStyled,
} from '../styled/RoamingOptionsStyled';
import { RoamingOptionDescription } from './RoamingOptionDescription';
import { RoamingOptionVolumeDescription } from './RoamingOptionVolumeDescription';
import { paymentStringWithComma } from './utils';


type TSelectedProduct = {
	productId: number;
	altInternetId?: number;
	optionalCallsId?: number;
};

type TRoamingOptionCardProps = IRoamingOption & {
	isConnectedOption?: boolean;
};

export const RoamingOptionCard: FC<TRoamingOptionCardProps> = ({
	calls,
	descriptionRef,
	id,
	internet,
	payment,
	services,
	subTitle,
	suitableFor,
	sms,
	title,
	isActivate,
}) => {
	const [cardTitle, setCardTitle] = useState(title);
	const [selectedProduct, setSelectedProduct] = useState<TSelectedProduct>({ productId: id });
	const [totalPrice, setTotalPrice] = useState('');
	const { altInternetId, optionalCallsId, productId } = selectedProduct;
	const dispatch = useDispatch();

	const { card } = useSelector(makeSelectTranslationPageProducts()).roamingOptions;
	const { currency } = useSelector(makeSelectTranslationCommon());
	const productMeta  = useSelector(makeSelectProductsMeta());
	const categorySelected = useSelector(makeSelectProductsCategorySelected()); 
	const roamingOptionsSelected = useSelector(makeSelectRoamingOptionsSelected());

	const isSelected = useMemo(() => {
		return !!roamingOptionsSelected.find(option => option.id === productId);
	}, [productId, roamingOptionsSelected]);
	
	const currentCart =
		productMeta.campaignServicesCategoryId === categorySelected
			? EProductsCart.CAMPAIGN
			: EProductsCart.PUBLIC;

	const onSelectRoamingOptionCard = useCallback(
        () => {
			dispatch(setTariffsAndServicesItemSelected({ cart: currentCart, id: productId }));
			dispatch(setSelectedRoamingOption({
				id: productId,
				title,
				subTitle,
				internet,
				services,
				calls,
				sms,
				suitableFor,
				payment,
				descriptionRef,
				isActivate,
			}))
		},
        [
			calls, 
			descriptionRef, 
			dispatch, 
			internet, 
			isActivate, 
			payment, 
			services, 
			sms, 
			subTitle, 
			suitableFor, 
			title,
			currentCart,
			productId,
		],
    );

	const changeProductIdSelected = useCallback((selectedId) => {
		dispatch(setTariffsAndServicesItemSelected({ cart: currentCart, id: productId }));
		dispatch(setTariffsAndServicesItemSelected(
			{ cart: currentCart, id: selectedId }),
		);
		dispatch(setSelectedRoamingOption({
			id: productId,
			title,
			subTitle,
			internet,
			services,
			calls,
			sms,
			suitableFor,
			payment,
			descriptionRef,
			isActivate,
		}));
		dispatch(setSelectedRoamingOption({
			id: selectedId,
			title,
			subTitle,
			internet,
			services,
			calls,
			sms,
			suitableFor,
			payment,
			descriptionRef,
			isActivate,
		}))
	},
	[
		calls, 
		currentCart, 
		descriptionRef, 
		dispatch, 
		internet, 
		isActivate, 
		payment, 
		productId, 
		services, 
		sms, 
		subTitle, 
		suitableFor, 
		title,
	]);

	// если карточка была открыта из категории "Выбранные услуги", то нужно установить нужный id
	useLayoutEffect(() => {
		if (id === internet?.altInternet?.callsOptionality?.id) {
			setSelectedProduct(({
				productId: internet?.altInternet?.callsOptionality?.id ?? id,
				altInternetId: internet?.altInternet?.id,
				optionalCallsId: calls?.callsOptionality?.id,
			}));
			return;
		} else if (id === internet?.altInternet?.id) {
			setSelectedProduct(({
				productId: internet?.altInternet?.id ?? id,
				altInternetId: internet?.altInternet?.id,
				optionalCallsId: undefined,
			}));
			return;
		} else if (id === calls?.callsOptionality?.id) {
			setSelectedProduct(({
				productId: calls?.callsOptionality?.id ?? id,
				optionalCallsId: calls?.callsOptionality?.id,
				altInternetId: undefined,
			}));
			return;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[]);

	useLayoutEffect(() => {
		if (
			roamingOptionsSelected.find(
				selectedOption => selectedOption.id === internet?.altInternet?.callsOptionality?.id,
			)
		) {
			setSelectedProduct(({
				productId: internet?.altInternet?.callsOptionality?.id ?? id,
				altInternetId: internet?.altInternet?.id,
				optionalCallsId: calls?.callsOptionality?.id,
			}));
			return;
		} else if (
			roamingOptionsSelected.find(
				selectedOption => selectedOption.id === internet?.altInternet?.id,
			)
		) {
			setSelectedProduct(({
				productId: internet?.altInternet?.id ?? id,
				altInternetId: internet?.altInternet?.id,
				optionalCallsId: undefined,
			}));
			return;
		} else if (
			roamingOptionsSelected.find(
				selectedOption => selectedOption.id === calls?.callsOptionality?.id,
			)
		) {
			setSelectedProduct(({
				productId: calls?.callsOptionality?.id ?? id,
				optionalCallsId: calls?.callsOptionality?.id,
				altInternetId: undefined,
			}));
			return;
		} 
	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[]);

	useEffect(() => {
		const costFactorNumber = payment?.costFactor ?? 1;
		const daysString = declinationWholeWord(
			costFactorNumber,
			`${card.payment.in} ${card.payment.oneDay}`,
			`${card.payment.for} ${costFactorNumber} ${card.payment.twoDays}`,
			`${card.payment.for} ${costFactorNumber} ${card.payment.fiveDays}`,
		);
		const paymentPeriod = payment?.costFactor ? daysString : payment?.period ?? daysString;

		switch (true) {
			case !!altInternetId && !!optionalCallsId:
				if (isSelected) {
					const selectedId = internet?.altInternet?.callsOptionality?.id ?? id;
					changeProductIdSelected(selectedId);
				}
				setCardTitle(internet?.altInternet?.callsOptionality?.optionTitle ?? title);
				setSelectedProduct(prevState => ({
					...prevState,
					productId: internet?.altInternet?.callsOptionality?.id ?? id,
				}));
				if (internet?.altInternet?.callsOptionality?.payment) {
					setTotalPrice(
						paymentStringWithComma(
							internet?.altInternet?.callsOptionality.payment,
							currency,
							paymentPeriod,
						),
					);
				} else {
					setTotalPrice('');
				}
				break;

			case !!altInternetId && !optionalCallsId:
				if (isSelected) {
					const selectedId = internet?.altInternet?.id ?? id;
					changeProductIdSelected(selectedId);
				}
				setCardTitle(internet?.altInternet?.title ?? title);
				setSelectedProduct(prevState => ({
					...prevState,
					productId: internet?.altInternet?.id ?? id,
				}));
				if (internet?.altInternet?.payment) {
					setTotalPrice(
						paymentStringWithComma(
							internet?.altInternet?.payment,
							currency,
							paymentPeriod,
						),
					);
				} else {
					setTotalPrice('');
				}
				break;
			case !!optionalCallsId && !altInternetId:
				if (isSelected) {
					const selectedId = calls?.callsOptionality?.id ?? id;
					changeProductIdSelected(selectedId);
				}
				setCardTitle(calls?.callsOptionality?.optionTitle ?? title);
				setSelectedProduct(prevState => ({
					...prevState,
					productId: calls?.callsOptionality?.id ?? id,
				}));
				if (calls?.callsOptionality?.payment) {
					setTotalPrice(
						paymentStringWithComma(
							calls?.callsOptionality?.payment,
							currency,
							paymentPeriod,
						),
					);
				} else {
					return setTotalPrice('');
				}
				break;
			case !altInternetId && !optionalCallsId:
				if (isSelected) {
					const selectedId = id;
					changeProductIdSelected(selectedId);
				}
				setCardTitle(title);
				setSelectedProduct(prevState => ({
					...prevState,
					productId: id,
				}));
				if (payment?.value) {
					setTotalPrice(paymentStringWithComma(payment?.value, currency, paymentPeriod));
				} else {
					setTotalPrice('');
				}
				break;
			default:
				if (isSelected) {
					const selectedId = id;
					changeProductIdSelected(selectedId);
				}
				setCardTitle(title);
				setSelectedProduct(prevState => ({
					...prevState,
					productId: id,
				}));
				if (payment?.value) {
					setTotalPrice(paymentStringWithComma(payment?.value, currency, paymentPeriod));
				} else {
					setTotalPrice('');
				}
				break;
		}
	}, [
		altInternetId,
		calls?.callsOptionality?.payment,
		calls?.callsOptionality?.id,
		calls?.callsOptionality?.optionTitle,
		id,
		internet?.altInternet?.callsOptionality?.optionTitle,
		internet?.altInternet?.callsOptionality?.payment,
		internet?.altInternet?.callsOptionality?.id,
		internet?.altInternet?.payment,
		internet?.altInternet?.title,
		internet?.altInternet?.id,
		optionalCallsId,
		payment?.costFactor,
		payment?.period,
		payment?.value,
		title,
		card,
		currency,
		isSelected,
		dispatch,
		currentCart,
		productId,
		changeProductIdSelected,
	]);

	const suitableForTooltip = useMemo(() => {
		if (selectedProduct.altInternetId && internet?.altInternet?.suitableFor) {
			return <Typography isDangerousInnerHTML>{internet?.altInternet?.suitableFor}</Typography>;
		} else if (suitableFor?.footnote) {
			return <Typography isDangerousInnerHTML>{suitableFor?.footnote}</Typography>;
		} else {
			return '';
		}
	}, [internet?.altInternet?.suitableFor, selectedProduct.altInternetId, suitableFor?.footnote]);

	const servicesTooltip = useMemo(() => {
		if (selectedProduct.optionalCallsId && calls?.callsOptionality?.servicesFootnote) {
			return <Typography isDangerousInnerHTML>{calls?.callsOptionality?.servicesFootnote}</Typography>;
		} else if (services?.footnote) {
			return <Typography isDangerousInnerHTML>{services.footnote}</Typography>;
		} else {
			return '';
		}
	}, [calls?.callsOptionality?.servicesFootnote, selectedProduct.optionalCallsId, services?.footnote]);

	const toggleOptionalCalls = () => {
		setSelectedProduct(prevState => ({
			...prevState,
			optionalCallsId: !prevState.optionalCallsId ? calls?.callsOptionality?.id : undefined,
		}));
	};

	return (
		<div>
			<RoamingOptionCardStyled isSelected={isSelected} onClick={onSelectRoamingOptionCard}>
				<RoamingOptionCardInfoStyled>
					<RoamingOptionDescription
						shortDescription={subTitle}
						title={cardTitle}
					/>
					{internet?.volume && (
						<RoamingOptionCardServiceInfoStyled>
							<RoamingOptionVolumeDescription
								defaultIcon={internetIcon}
								footnoteTooltip={internet?.footnote ?? ''}
								iconUrl={internet?.iconUrl ?? ''}
								title={internet?.title ?? ''}
							/>
							<RoamingOptionCardServiceDescriptionStyled>
								{internet?.altInternet ? (
									<>
										<RoamingOptionIntenetButtonStyled
											color={!selectedProduct.altInternetId ? 'secondary' : 'white'}
											size='small'
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation();
												setSelectedProduct(prevState => ({
													...prevState,
													altInternetId: undefined,
												}))}
											}
										>
											{`${internet?.volume.value ?? ''} ${internet?.volume.unit ?? ''}`}
										</RoamingOptionIntenetButtonStyled>
										<RoamingOptionIntenetButtonStyled
											color={selectedProduct.altInternetId ? 'secondary' : 'white'}
											size='small'
											onClick={(e: React.MouseEvent) => {
												e.stopPropagation();
												setSelectedProduct(prevState => ({
													...prevState,
													altInternetId: internet?.altInternet?.id,
												}))}
											}
										>
											{`${internet?.altInternet?.volume?.value ?? ''} ${
												internet?.altInternet?.volume?.unit ?? ''
											}`}
										</RoamingOptionIntenetButtonStyled>
									</>
								) : (
									<Typography
										component='span'
										variant='body1bold'
									>
										{`${internet?.volume.value ?? ''} ${internet?.volume.unit ?? ''}`}
									</Typography>
								)}
							</RoamingOptionCardServiceDescriptionStyled>
						</RoamingOptionCardServiceInfoStyled>
					)}
					{services?.volume && (
						<RoamingOptionCardServiceInfoStyled>
							<RoamingOptionVolumeDescription
								defaultIcon={servicesIcon}
								footnoteTooltip={servicesTooltip}
								iconUrl={services.iconUrl ?? ''}
								title={services.title ?? ''}
							/>
							<RoamingOptionCardServiceDescriptionStyled>
								<Typography
									component='span'
									variant='body1bold'
								>
									{services.volume ?? ''}
								</Typography>
							</RoamingOptionCardServiceDescriptionStyled>
						</RoamingOptionCardServiceInfoStyled>
					)}
					{(!calls?.callsOptionality || selectedProduct.optionalCallsId) && calls?.volume && (
						<RoamingOptionCardServiceInfoStyled>
							<RoamingOptionVolumeDescription
								defaultIcon={callsIcon}
								footnoteTooltip={calls?.footnote ?? ''}
								iconUrl={calls?.iconUrl ?? ''}
								title={calls?.title ?? ''}
							/>
							<RoamingOptionCardServiceDescriptionStyled>
								<Typography
									component='span'
									variant='body1bold'
								>
									{`${calls?.volume.value ?? ''} ${calls?.volume.unit ?? ''}`}
								</Typography>
							</RoamingOptionCardServiceDescriptionStyled>
						</RoamingOptionCardServiceInfoStyled>
					)}
					{calls?.callsOptionality && (
						<RoamingOptionCallsButtonStyled
							size='small'
							variant='text'
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								toggleOptionalCalls()}
							}
						>
							<Typography
								color={
									selectedProduct.optionalCallsId ? colors.textColorDanger : colors.textColorSuccess}
								component='span'
								variant='body1bold'
							>
								{selectedProduct.optionalCallsId
									? card.buttons.removeOptionalCalls
									: card.buttons.addOptionalCalls}
							</Typography>
						</RoamingOptionCallsButtonStyled>
					)}
					{sms?.volume && (
						<RoamingOptionCardServiceInfoStyled>
							<RoamingOptionVolumeDescription
								defaultIcon={smsIcon}
								footnoteTooltip={sms.footnote ?? ''}
								iconUrl={sms.iconUrl ?? ''}
								title={sms.title ?? ''}
							/>
							<RoamingOptionCardServiceDescriptionStyled>
								<Typography
									component='span'
									variant='body1bold'
								>
									{sms.volume ?? ''}
								</Typography>
							</RoamingOptionCardServiceDescriptionStyled>
						</RoamingOptionCardServiceInfoStyled>
					)}
				</RoamingOptionCardInfoStyled>
				<RoamingOptionCardBottom>
					{suitableForTooltip && (
						<RoamingOptionCardServiceInfoStyled>
							<RoamingOptionVolumeDescription
								defaultIcon={peopleIcon}
								footnoteTooltip={suitableForTooltip}
								iconUrl={suitableFor?.iconUrl ?? ''}
								isTitleBold={true}
								title={card.suitableFor}
							/>
						</RoamingOptionCardServiceInfoStyled>
					)}
					<DividerHrStyled />
					<Typography variant='h3'>{totalPrice}</Typography>
					{descriptionRef && (
						<LinkContainer>
							<ExternalLink
								href={descriptionRef ?? '#'}
								target={ELinkTarget.BLANK}
							>
								<Typography
									color={colors.textLinkColor}
									component='span'
									variant='h5'
								>
									{card.buttons.moreInfo}
								</Typography>
							</ExternalLink>
						</LinkContainer>
					)}
				</RoamingOptionCardBottom>
			</RoamingOptionCardStyled>
		</div>
	);
};
